import { useState } from "react";
import { Button, Card, Field, Input, Spinner } from "@/components/primitives";
import { Icon } from "@/components/Icon";
import {
  signIn,
  cognitoConfigured,
  requestPasswordReset,
  confirmPasswordReset,
  requestMagicLink,
  type NewPasswordChallenge,
} from "@/lib/auth";
import { googleSignInEnabled, startGoogleSignIn } from "@/lib/oauth";

type Mode = "signIn" | "resetRequest" | "resetConfirm" | "magicRequest" | "magicSent";

interface Props {
  onSignedIn: () => void;
}

export function SignIn({ onSignedIn }: Props) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [challenge, setChallenge] = useState<NewPasswordChallenge | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [mode, setMode] = useState<Mode>("signIn");
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const switchMode = (next: Mode) => {
    setMode(next);
    setError(null);
    setInfo(null);
    setNewPassword("");
    setConfirmPassword("");
    setResetCode("");
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await signIn(email, password);
      if ("kind" in result) {
        setChallenge(result);
      } else {
        onSignedIn();
      }
    } catch (err) {
      const message = (err as Error).message;
      // Cognito puts users in RESET_REQUIRED after AdminResetUserPassword or temp-password
      // expiry. The SDK message is "Password reset required for the user" — surface a
      // clearer next step rather than dumping the raw error.
      if (/password reset required/i.test(message)) {
        setError(
          "Your temporary password has expired or was reset. Use 'Forgot password' below to receive a reset code by email.",
        );
      } else {
        setError(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onRequestMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await requestMagicLink(email);
      // Cognito issues a challenge for unknown addresses too, so this wording
      // must not imply the account exists.
      setMode("magicSent");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const onRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setSubmitting(true);
    try {
      await requestPasswordReset(email);
      // Cognito's ForgotPassword API returns success even when the user is in
      // FORCE_CHANGE_PASSWORD (seeded but never activated) and silently does
      // not send. We can't distinguish that state from the unauthenticated
      // client, so set expectations honestly instead of promising delivery.
      setInfo(
        "If your account is active, a reset code is on its way. If you haven’t finished setting up your account yet, use the temporary password from your original invite email instead.",
      );
      setMode("resetConfirm");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const onConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (newPassword !== confirmPassword) {
      setError("Passwords don’t match.");
      return;
    }
    if (newPassword.length < 12) {
      setError("Password must be at least 12 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await confirmPasswordReset(email, resetCode, newPassword);
      const result = await signIn(email, newPassword);
      if ("kind" in result) {
        setChallenge(result);
        switchMode("signIn");
      } else {
        onSignedIn();
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const onSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (newPassword !== confirmPassword) {
      setError("Passwords don’t match.");
      return;
    }
    if (newPassword.length < 12) {
      setError("Password must be at least 12 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await challenge!.complete(newPassword);
      onSignedIn();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: "var(--background)",
        padding: 24,
      }}
    >
      <Card style={{ width: 380, padding: 24 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 18,
          }}
        >
          <span
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: "var(--primary)",
              color: "var(--primary-foreground)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon name="pulse" size={18} />
          </span>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>Status Watch</div>
          </div>
        </div>

        {!cognitoConfigured && (
          <div
            style={{
              padding: 12,
              borderRadius: 8,
              background:
                "color-mix(in oklch, var(--warning) 12%, transparent)",
              color: "var(--warning-foreground)",
              fontSize: 12,
              marginBottom: 16,
              lineHeight: 1.5,
            }}
          >
            Cognito is not configured. Set <code>VITE_COGNITO_USER_POOL_ID</code>{" "}
            and <code>VITE_COGNITO_CLIENT_ID</code> in your environment.
          </div>
        )}

        {challenge ? (
          <form
            onSubmit={onSetPassword}
            style={{ display: "flex", flexDirection: "column", gap: 14 }}
          >
            <div
              style={{
                fontSize: 13,
                color: "var(--muted-foreground)",
                lineHeight: 1.5,
              }}
            >
              Welcome. Set a permanent password — at least 12 characters with
              upper- and lowercase letters and a number.
            </div>
            <Field label="New password">
              <Input
                value={newPassword}
                onChange={setNewPassword}
                placeholder="••••••••••••"
                type="password"
              />
            </Field>
            <Field label="Confirm new password">
              <Input
                value={confirmPassword}
                onChange={setConfirmPassword}
                placeholder="••••••••••••"
                type="password"
              />
            </Field>
            {error && <ErrorBox msg={error} />}
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner />
                  Setting password…
                </>
              ) : (
                "Set password and sign in"
              )}
            </Button>
          </form>
        ) : mode === "magicSent" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div
              style={{
                fontSize: 13,
                color: "var(--muted-foreground)",
                lineHeight: 1.5,
              }}
            >
              If <strong>{email}</strong> has an account, a sign-in link is on
              its way. It expires in 15 minutes and works once.
            </div>
            <InfoBox msg="Open the link in this browser — it won’t work on another device." />
            <LinkButton onClick={() => switchMode("signIn")}>
              Back to sign in
            </LinkButton>
          </div>
        ) : mode === "magicRequest" ? (
          <form
            onSubmit={onRequestMagicLink}
            style={{ display: "flex", flexDirection: "column", gap: 14 }}
          >
            <div
              style={{
                fontSize: 13,
                color: "var(--muted-foreground)",
                lineHeight: 1.5,
              }}
            >
              We’ll email you a link that signs you in — no password needed.
            </div>
            <Field label="Email">
              <Input
                value={email}
                onChange={setEmail}
                placeholder="you@example.com"
                type="email"
              />
            </Field>
            {error && <ErrorBox msg={error} />}
            <Button
              type="submit"
              variant="primary"
              disabled={submitting || !cognitoConfigured}
            >
              {submitting ? (
                <>
                  <Spinner />
                  Sending link…
                </>
              ) : (
                "Email me a sign-in link"
              )}
            </Button>
            <LinkButton onClick={() => switchMode("signIn")}>
              Use a password instead
            </LinkButton>
          </form>
        ) : mode === "resetRequest" ? (
          <form
            onSubmit={onRequestReset}
            style={{ display: "flex", flexDirection: "column", gap: 14 }}
          >
            <div
              style={{
                fontSize: 13,
                color: "var(--muted-foreground)",
                lineHeight: 1.5,
              }}
            >
              Enter your email and we’ll send you a code to set a new password.
            </div>
            <Field label="Email">
              <Input
                value={email}
                onChange={setEmail}
                placeholder="you@example.com"
                type="email"
              />
            </Field>
            {info && <InfoBox msg={info} />}
            {error && <ErrorBox msg={error} />}
            <Button
              type="submit"
              variant="primary"
              disabled={submitting || !cognitoConfigured}
            >
              {submitting ? (
                <>
                  <Spinner />
                  Sending code…
                </>
              ) : (
                "Send reset code"
              )}
            </Button>
            <LinkButton onClick={() => switchMode("signIn")}>
              Back to sign in
            </LinkButton>
          </form>
        ) : mode === "resetConfirm" ? (
          <form
            onSubmit={onConfirmReset}
            style={{ display: "flex", flexDirection: "column", gap: 14 }}
          >
            <div
              style={{
                fontSize: 13,
                color: "var(--muted-foreground)",
                lineHeight: 1.5,
              }}
            >
              Enter the code from your email and set a new password — at least
              12 characters with upper- and lowercase letters and a number.
            </div>
            <Field label="Reset code">
              <Input
                value={resetCode}
                onChange={setResetCode}
                placeholder="123456"
                type="text"
              />
            </Field>
            <Field label="New password">
              <Input
                value={newPassword}
                onChange={setNewPassword}
                placeholder="••••••••••••"
                type="password"
              />
            </Field>
            <Field label="Confirm new password">
              <Input
                value={confirmPassword}
                onChange={setConfirmPassword}
                placeholder="••••••••••••"
                type="password"
              />
            </Field>
            {info && <InfoBox msg={info} />}
            {error && <ErrorBox msg={error} />}
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner />
                  Setting password…
                </>
              ) : (
                "Set password and sign in"
              )}
            </Button>
            <LinkButton onClick={() => switchMode("signIn")}>
              Back to sign in
            </LinkButton>
          </form>
        ) : (
          <form
            onSubmit={onSubmit}
            style={{ display: "flex", flexDirection: "column", gap: 14 }}
          >
            <Field label="Email">
              <Input
                value={email}
                onChange={setEmail}
                placeholder="you@example.com"
                type="email"
              />
            </Field>
            <Field label="Password">
              <Input
                value={password}
                onChange={setPassword}
                placeholder="••••••••"
                type="password"
              />
            </Field>
            {error && <ErrorBox msg={error} />}
            <Button
              type="submit"
              variant="primary"
              disabled={submitting || !cognitoConfigured}
            >
              {submitting ? (
                <>
                  <Spinner />
                  Signing in…
                </>
              ) : (
                "Sign in"
              )}
            </Button>
            {/* Hidden unless the pool actually has the Google IdP — a visible
                button without it would dead-end on a Cognito error. */}
            {googleSignInEnabled && (
              <>
                <Divider />
                <Button
                  type="button"
                  onClick={() => {
                    setError(null);
                    startGoogleSignIn().catch((err) =>
                      setError((err as Error).message),
                    );
                  }}
                >
                  Continue with Google
                </Button>
              </>
            )}
            <LinkButton onClick={() => switchMode("magicRequest")}>
              Email me a sign-in link instead
            </LinkButton>
            <LinkButton onClick={() => switchMode("resetRequest")}>
              Forgot password?
            </LinkButton>
          </form>
        )}
      </Card>
    </div>
  );
}

function Divider() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        color: "var(--muted-foreground)",
        fontSize: 11,
      }}
    >
      <span style={{ flex: 1, height: 1, background: "var(--border)" }} />
      or
      <span style={{ flex: 1, height: 1, background: "var(--border)" }} />
    </div>
  );
}

function ErrorBox({ msg }: { msg: string }) {
  return (
    <div
      style={{
        fontSize: 12,
        color: "var(--destructive)",
        padding: 10,
        borderRadius: 8,
        background:
          "color-mix(in oklch, var(--destructive) 10%, transparent)",
      }}
    >
      {msg}
    </div>
  );
}

function InfoBox({ msg }: { msg: string }) {
  return (
    <div
      style={{
        fontSize: 12,
        color: "var(--muted-foreground)",
        padding: 10,
        borderRadius: 8,
        background: "color-mix(in oklch, var(--primary) 8%, transparent)",
      }}
    >
      {msg}
    </div>
  );
}

function LinkButton({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: "none",
        border: 0,
        padding: 0,
        cursor: "pointer",
        color: "var(--primary)",
        fontSize: 12,
        alignSelf: "center",
        textDecoration: "underline",
      }}
    >
      {children}
    </button>
  );
}
